import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #isRenderingNextPage = false;

  /** @type {boolean} */
  #isRenderingPreviousPage = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    try {
      if (!page || !this.#shouldUsePage(page)) return;

      await this.#fetchSpecificPage(page.page, page.url);
    } catch {
      // Leave the page uncached and let the next scroll trigger retry it. Callers invoke this
      // without awaiting, so rethrowing would only surface an unhandled rejection.
    } finally {
      // Must settle even when the fetch fails. A waiting render blocks on this promise and clears
      // its re-entrancy guard in a `finally`, so never settling would disable infinite scroll for
      // the rest of the page's life.
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid || this.#isRenderingNextPage) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    this.#isRenderingNextPage = true;

    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      // Re-check after awaiting the fetch, in case the grid changed while we waited.
      if (this.#getRenderedPages().has(nextPage.page)) return;

      grid.append(...this.#withoutRenderedCards(nextPageItemElements));

      this.#aspectRatioHelper?.processNewElements();

      history.pushState('', '', nextPage.url.toString());
    } finally {
      this.#isRenderingNextPage = false;
    }

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid || this.#isRenderingPreviousPage) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    this.#isRenderingPreviousPage = true;

    try {
      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      // Re-check after awaiting the fetch, in case the grid changed while we waited.
      if (this.#getRenderedPages().has(previousPage.page)) return;

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...this.#withoutRenderedCards(previousPageItemElements));

      this.#aspectRatioHelper?.processNewElements();

      history.pushState('', '', previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }
    } finally {
      this.#isRenderingPreviousPage = false;
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const renderedPages = this.#getRenderedPages();

    if (!renderedPages.size) return;

    const pages = [...renderedPages];
    const page = type === 'previous' ? Math.min(...pages) - 1 : Math.max(...pages) + 1;

    if (renderedPages.has(page)) return;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * Reads the pages currently in the grid straight from the DOM.
   *
   * This deliberately avoids `refs`, which is refreshed asynchronously by a mutation observer and so
   * can still describe the previous state. It is also read fresh on every call rather than cached,
   * so a filter or sort re-render can never leave us acting on pages that are no longer on screen.
   *
   * @returns {Set<number>} The page numbers currently rendered.
   */
  #getRenderedPages() {
    const pages = new Set();

    for (const card of this.#getCardElements()) {
      const page = Number(card.dataset.page);

      if (Number.isInteger(page)) pages.add(page);
    }

    return pages;
  }

  /**
   * Drops cards for products that are already in the grid, so a late or repeated render can never
   * surface the same product twice.
   *
   * @param {NodeListOf<Element>} elements
   * @returns {Element[]}
   */
  #withoutRenderedCards(elements) {
    const renderedProductIds = new Set(
      this.#getCardElements()
        .map((card) => card.dataset.productId)
        .filter(Boolean)
    );

    return [...elements].filter((element) => {
      const productId = element instanceof HTMLElement ? element.dataset.productId : undefined;

      return !productId || !renderedProductIds.has(productId);
    });
  }

  /**
   * @returns {HTMLElement[]} The card elements currently in the grid.
   */
  #getCardElements() {
    const grid = this.refs.grid ?? this.querySelector('[ref="grid"]');

    if (!grid) return [];

    return /** @type {HTMLElement[]} */ ([...grid.querySelectorAll(':scope > [ref="cards[]"]')]);
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
